import { CallhomeApi } from 'clients';
import { type IEvent } from 'clients/PlatformApi';
import useShareHasMissingVideo from 'components/EventsPage/Event/useShareHasMissingVideo';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import PollCloudStore from 'stores/PollCloudStore';
import {
  CLOUD_STATUS_COMPLETE,
  CLOUD_STATUS_COMPLETE_WITH_ERRORS,
  CLOUD_STATUS_ERROR,
  CLOUD_STATUS_IN_PROGRESS,
  CLOUD_STATUS_VERIFY,
} from 'vd-constants';

/** Identifies one push-to-cloud badge. Each skin maps the key to its own icon and style. */
export type CloudStatusBadgeKey =
  'deviceOffline' | 'done' | 'doneWithErrors' | 'error' | 'uploading' | 'verify';

export interface CloudStatusBadge {
  key: CloudStatusBadgeKey;
  tooltip: string;
}

/** The locale strings that the push-to-cloud badges need. */
export interface CloudStatusBadgesLocale {
  faceblur: {
    verifyStatusTooltip: string;
  };
  pushToCloudState: {
    deviceOfflineDuringPushToCloud: string;
    done: string;
    doneWithErrors: string;
    doneWithErrorsVideoNotPushed: string;
    error: string;
    uploading: string;
  };
}

/**
 * Select the push-to-cloud badges to show, with the tooltip of each one.
 * More than one badge can show at the same time.
 * The result holds keys and text only, so each skin picks its own icons.
 * The Legacy cloud status and the Aspen cloud status both call this function.
 */
export function getCloudStatusBadges({
  cloudStatus,
  isDeviceOffline,
  isInProgress,
  locale,
  shareMissVideo,
}: {
  cloudStatus: string | null | undefined;
  isDeviceOffline: boolean;
  isInProgress?: boolean;
  locale: CloudStatusBadgesLocale;
  shareMissVideo: boolean;
}): CloudStatusBadge[] {
  const badges: CloudStatusBadge[] = [];

  // The order of the pushes sets the order the badges appear in.
  if (cloudStatus === CLOUD_STATUS_IN_PROGRESS && isDeviceOffline) {
    badges.push({
      key: 'deviceOffline',
      tooltip: locale.pushToCloudState.deviceOfflineDuringPushToCloud,
    });
  }

  if (!isInProgress && cloudStatus === CLOUD_STATUS_COMPLETE && !shareMissVideo) {
    badges.push({ key: 'done', tooltip: locale.pushToCloudState.done });
  }

  if (!isInProgress && cloudStatus === CLOUD_STATUS_ERROR) {
    badges.push({ key: 'error', tooltip: locale.pushToCloudState.error });
  }

  if (!isInProgress && cloudStatus === CLOUD_STATUS_VERIFY) {
    badges.push({ key: 'verify', tooltip: locale.faceblur.verifyStatusTooltip });
  }

  if (!isInProgress && (cloudStatus === CLOUD_STATUS_COMPLETE_WITH_ERRORS || shareMissVideo)) {
    badges.push({
      key: 'doneWithErrors',
      tooltip: shareMissVideo
        ? locale.pushToCloudState.doneWithErrorsVideoNotPushed
        : locale.pushToCloudState.doneWithErrors,
    });
  }

  if (isInProgress || cloudStatus === CLOUD_STATUS_IN_PROGRESS) {
    badges.push({ key: 'uploading', tooltip: locale.pushToCloudState.uploading });
  }

  return badges;
}

/**
 * Resolve the push-to-cloud state of an event and turn it into badges.
 * The caller renders a badge for each key and hides everything when `cloudStatus` is falsy.
 * The Legacy cloud status and the Aspen cloud status both call this hook.
 */
export function useCloudStatusBadges({
  event,
  eventStatus,
  isInProgress,
  locationId,
  pollingStatus,
  tenantId,
}: {
  event: IEvent;
  eventStatus?: string;
  isInProgress?: boolean;
  locationId?: string;
  pollingStatus?: string | null;
  tenantId: string;
}) {
  const locale = useLocale();
  const shareMissVideo = useShareHasMissingVideo(event, tenantId);

  const [isDeviceOffline, setIsDeviceOffline] = React.useState(false);

  const cloudStatus = React.useMemo(
    () => PollCloudStore.getPrioritizedState({}, pollingStatus, eventStatus),
    [pollingStatus, eventStatus],
  );

  React.useEffect(() => {
    if (locationId && cloudStatus === CLOUD_STATUS_IN_PROGRESS) {
      CallhomeApi()
        .isDeviceOffline(locationId)
        .then((isDeviceOffline) => {
          setIsDeviceOffline(isDeviceOffline);
        });
    }
  }, [locationId, cloudStatus]);

  const badges = React.useMemo(
    () =>
      getCloudStatusBadges({
        cloudStatus,
        isDeviceOffline,
        isInProgress,
        locale,
        shareMissVideo,
      }),
    [cloudStatus, isDeviceOffline, isInProgress, locale, shareMissVideo],
  );

  return { badges, cloudStatus };
}
